import React from 'react';
import jwtDecode from 'jwt-decode';
import {connect} from 'react-redux';
import {Button, SVG} from '@br/br-components';

import StorageAPI from '../../apis/storage_api';
import * as AnalyticsActions from '../../actions/analyticsActions';
import * as AppActions from '../../actions/appActions';
import * as NotificationActions from '../../actions/notificationActions';
import * as RegisterAPI from '../../apis/register_api';
import * as UserActions from '../../actions/userActions';
import {redirectUser, update} from '../../actions/userActions';

import * as TopSelectors from '../../selectors/topLevelSelectors';

import ChooseSignupMethod from '../molecules/chooseSignupMethod';
import EmailSignup from '../molecules/emailSignup';
import EmailVerifyNotice from '../molecules/emailVerifyNotice';
import EnterYourNames from '../molecules/enterYourNames';
import FlowController from '../atoms/flowController';
import Notification from '../molecules/notification';
import PhoneSignup from '../molecules/phoneSignup';
import PhoneVerify from '../molecules/phoneVerify';
import PickUserName from '../molecules/pickUserName';
import {googleTrackingPixels} from '../molecules/video/analytics/gtagEvents';

const getData = () => {
  const cookieData = StorageAPI.get('br_registration') || '{}';
  return JSON.parse(cookieData);
};

export class SignupPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.closeEmailNotification = this.closeEmailNotification.bind(this);
    this.updateAccount = this.updateAccount.bind(this);
    this.onNext = this.onNext.bind(this);
    this.trackAccountCreated = this.trackAccountCreated.bind(this);
    let currentFlowStep = 'choosemethod';
    if (global.location && global.location.hash) {
      currentFlowStep = global.location.hash.replace('#', '');
    }
    this.state = {
      currentFlowStep,
      referrer: '',
    };
  }

  componentDidMount() {
    global.onpopstate = () => {
      const currentFlowStep = global.location.hash.replace('#', '') || 'choosemethod';
      this.setState({currentFlowStep});
      this._flow.next(currentFlowStep);
    };
  }

  trackAccountCreationAction = () => {
    //This function will be called once the account creation is done and successful
    const url = this.props.returnUrl || this.props.ui.brHost;
    googleTrackingPixels(this.props.pageType, url);
  };
  closeEmailNotification() {
    this.props.notificationCallbacks.updateNotificationData('email_confirmation', false);
  }

  onBack() {
    global.history.back();
  }

  onNext(prev, next) {
    const hash = next.flowKey === 'choosemethod' ? '' : `#${next.flowKey}`;
    if (next.index > prev.index) {
      global.history.pushState({}, '', hash);
    }
    this.setState(
      {
        currentFlowStep: next.flowKey,
        referrer: this.state.referrer || global.document?.referrer,
      },
      () => {
        this.props.trackEvent({
          page_type: 'other',
          tag_manager_event: 'page_type',
          referring_source: this.state.referrer,
        });
      }
    );
  }

  trackAccountCreated(options = {}) {
    const {returnUrl, screen} = this.props;
    const app = returnUrl && returnUrl.includes('bracketchallenge') ? 'BCG' : 'BR';

    this.props.trackEvent({
      accountType: this.state.currentFlowStep,
      app,
      legacyAccount: false,
      screen,
      socialOnboarding: false,
      success: true,
      tag_manager_event: 'account_created',
      ...options,
    });
  }

  updateAccount(info) {
    const userID = this.props.user.id || StorageAPI.get('br_userid', true);
    return this.props.updateUserAccount({info, userID});
  }

  render() {
    const registrationData = this.props.getData();
    const userName = registrationData && registrationData.username;
    const backButton = (
      <Button classes="flowBack" onClick={this.onBack}>
        <SVG type="icon" target="leftArrow" />
        Back
      </Button>
    );

    return (
      <div className="page signupPage">
        <Notification
          {...this.props.notificationCallbacks}
          data={this.props.notificationsData}
          name="email_confirmation"
          ui={this.props.ui}
        >
          <p>
            Email confirmation sent. You have 24 hours to claim{' '}
            <span>{userName || 'your username'}</span>.
            <Button onClick={this.closeEmailNotification}>Dismiss</Button>
          </p>
        </Notification>
        <FlowController
          onFlowFinished={this.props.signupSuccess}
          startAt={this.state.currentFlowStep}
          onNext={this.onNext}
          ref={(ref) => (this._flow = ref)}
        >
          <ChooseSignupMethod
            flowKey="choosemethod"
            loadScript={this.props.loadScript}
            onFbOauth={this.props.onFbOauth}
            methodChosen={this.props.signupMethodChosen}
            returnUrl={this.props.returnUrl}
            trackEvent={this.props.trackEvent}
            showEmailSignup={this.props.ui.showEmailSignup}
            disableFbSignup={this.props.ui.disableFbSignup}
          />
          <EnterYourNames
            field={this.props.user.field}
            flowKey="name"
            firstName={registrationData.first_name}
            lastName={registrationData.last_name}
            status={this.props.user.status}
            updateData={this.props.updateRegistration}
            validateName={this.props.validateName}
          >
            {backButton}
          </EnterYourNames>
          <PickUserName
            flowKey="username"
            authMethod={registrationData.method}
            checkUsername={this.props.checkUsername}
            saveUsername={this.props.saveUsername}
            updateData={this.props.updateRegistration}
            trackEvent={this.props.trackEvent}
            trackAction={this.trackAccountCreationAction}
          >
            {backButton}
          </PickUserName>
          <FlowController flowKey="phone">
            <PhoneSignup
              createAccount={this.props.createPhoneAccount}
              trackEvent={this.props.trackEvent}
              mParticleLogin={this.props.mParticleLogin}
              mParticleUpdateAttributes={this.props.mParticleUpdateAttributes}
            >
              {backButton}
            </PhoneSignup>
            <PhoneVerify
              verifyPhone={this.props.verifyPhoneAccount}
              authorizePhone={this.props.authorizePhoneAccount}
              trackAction={this.trackAccountCreationAction}
              trackEvent={this.props.trackEvent}
              onboarding={true}
              isSignup={true}
              mParticleLogin={this.props.mParticleLogin}
            >
              {backButton}
            </PhoneVerify>
          </FlowController>
          {this.props.ui.showEmailSignup && (
            <FlowController flowKey="email">
              <EmailSignup
                createAccount={this.props.createAccount}
                mParticleLogin={this.props.mParticleLogin}
                mParticleUpdateAttributes={this.props.mParticleUpdateAttributes}
                onboarding={true}
                trackEvent={this.props.trackEvent}
                screen={this.props.screen}
                trackAction={this.trackAccountCreationAction}
              >
                {backButton}
              </EmailSignup>
              <EmailVerifyNotice />
            </FlowController>
          )}
          <FlowController flowKey="facebook" />
        </FlowController>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    notificationsData: TopSelectors.notification(state),
    pageType: state.page.type,
    returnUrl: TopSelectors.page(state).returnUrl,
    screen: TopSelectors.page(state).screen,
    ui: TopSelectors.ui(state),
    user: TopSelectors.user(state),
  };
};

function mapDispatchToProps(dispatch, {store}) {
  const userAgent = store.getState().ui.userAgent;
  // temporary redirecting users to accounts so needed cookies are set before any logged user reach the home
  const signupSuccessUrl = '/accounts/edit';
  const dispatchProps = {
    authorizePhoneAccount: (accountData) => dispatch(UserActions.requestPhoneVerify(accountData)),
    checkUsername: (username) => {
      return RegisterAPI.isUsernameAvailable(username);
    },
    createAccount: (accountData) =>
      RegisterAPI.createUserWithEmail(accountData).then((auth) => {
        // This is currently required after moving bday form to show before email confirmation
        const {sub: id} = jwtDecode(auth.access_token);
        StorageAPI.setAccountCookies({auth});
        StorageAPI.set('br_userid', id, {ttl: StorageAPI.oneMonth});
      }),
    createFacebookAccount: (accountData, callback) =>
      RegisterAPI.createUserWithFb(accountData)
        .then((auth) => {
          const {sub: id} = jwtDecode(auth.access_token);
          StorageAPI.set('br_registration', '{}');
          StorageAPI.setAccountCookies({auth});
          StorageAPI.set('br_userid', id, {ttl: StorageAPI.oneMonth});
          dispatchProps.mParticleLogin(
            {},
            {
              gatekeeper_id: id,
              account_verified: true,
              loggedIn: false,
              signup_source: 'facebook',
            }
          );
          callback();
        })
        .catch((err) => {
          throw err;
        }),
    createPhoneAccount: (accountData) => RegisterAPI.createUserWithPhone(accountData),
    getData,
    loadScript: (script) => dispatch(AppActions.loadScript(script)),
    notificationCallbacks: {
      checkNotification: (data) => dispatch(NotificationActions.checkNotification(data, false)),
      updateNotificationData: (name, value) => {
        return Promise.all([
          dispatch(NotificationActions.updateNotificationData({name, value})),
          dispatch(NotificationActions.checkNotification(name, false)),
        ]);
      },
    },
    mParticleLogin: (event, userAttributes) =>
      dispatch(AnalyticsActions.mParticleLogin(userAgent, event, userAttributes)),
    mParticleUpdateAttributes: (event) =>
      dispatch(AnalyticsActions.mParticleUpdateAttributes(event)),
    onFbOauth: (payload) => {
      const facebook = {
        device: 'web',
        token: payload.accessToken,
        uid: payload.userID,
      };
      dispatchProps.updateRegistration({facebook});
    },
    saveUsername: (username, callback) => {
      return RegisterAPI.reserveUsername(username).then(() => {
        dispatchProps.updateRegistration({username});

        // For the Facebook creation flow, after saving the username
        // we create the account in order to have all the info needed for the request
        const data = dispatchProps.getData();
        if (data.method === 'facebook') {
          dispatchProps.createFacebookAccount(data, callback);
        }
      });
    },
    signupMethodChosen: (method) => {
      dispatchProps.updateRegistration({method});
    },
    signupSuccess: () => dispatch(redirectUser(signupSuccessUrl)),
    trackEvent: (event) => dispatch(AnalyticsActions.trackEvent(event)),
    updateRegistration: (data) => {
      const cookieData = StorageAPI.get('br_registration') || '{}';
      const state = {
        ...JSON.parse(cookieData),
        ...data,
      };
      StorageAPI.set('br_registration', JSON.stringify(state));
      return Promise.resolve('updated registration');
    },
    updateUserAccount: (data) => dispatch(update(data)),
    validateName: (data) => dispatch(UserActions.validateName(data)),
    verifyPhoneAccount: (accountData) => dispatch(UserActions.submitPhoneVerify(accountData)),
  };
  return dispatchProps;
}

export default connect(mapStateToProps, mapDispatchToProps)(SignupPage);
